import { X } from 'lucide-react';
import React, { useEffect } from 'react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Spinner } from '../../../../components/spinner';
import { EnrichedTrack, SelectedWorkout } from '../types';
import { useTrackData } from '../utils/useTrackData';
import { TrackStatsCard } from './TrackStatsCard';

interface TrackLoaderProps {
    workout: SelectedWorkout;
    isFocused: boolean;
    onFocus: () => void;
    onRemove: () => void;
    onTrackChange: (id: number, track: EnrichedTrack | null) => void;
}

const DismissibleAlert = ({
    variant,
    onClose,
    children
}: {
    variant: 'info' | 'destructive';
    onClose: () => void;
    children: React.ReactNode;
}) => (
    <Alert variant={variant} className="min-w-[220px] py-2 pr-10">
        <AlertDescription>{children}</AlertDescription>
        <Button
            variant="ghost"
            size="icon-xs"
            className="absolute top-1.5 right-1.5"
            onClick={onClose}
            aria-label="Remove"
        >
            <X />
        </Button>
    </Alert>
);

export function TrackLoader({
    workout,
    isFocused,
    onFocus,
    onRemove,
    onTrackChange
}: TrackLoaderProps) {
    const state = useTrackData(workout.id);

    useEffect(() => {
        onTrackChange(workout.id, state.track);
        return () => onTrackChange(workout.id, null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.track, workout.id]);

    if (state.loading) {
        return (
            <div
                className="flex min-w-[220px] items-center gap-2 rounded-lg border border-dashed bg-card p-2.5 text-xs text-muted-foreground"
                style={{ borderColor: workout.color }}
            >
                <Spinner size="sm" />
                <span>Loading {workout.label}…</span>
            </div>
        );
    }

    if (state.noGpx) {
        return (
            <DismissibleAlert variant="info" onClose={onRemove}>
                {workout.label}: no GPX
            </DismissibleAlert>
        );
    }

    if (state.error) {
        return (
            <DismissibleAlert variant="destructive" onClose={onRemove}>
                {workout.label}: {state.error}
            </DismissibleAlert>
        );
    }

    if (!state.track) return null;

    return (
        <TrackStatsCard
            track={state.track}
            workout={workout}
            isFocused={isFocused}
            onFocus={onFocus}
            onRemove={onRemove}
        />
    );
}
